import { Cpu, Building2, Truck, Battery, Grid3X3, Plug, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem, fadeInUp } from "@/components/animations/ScrollAnimations";

const solutions = [
  {
    icon: Cpu,
    title: "SaaS Platform",
    description: "White-label charging management with real-time monitoring, smart pricing, and comprehensive analytics dashboard.",
    highlight: "Multi-tenant",
    isPrimary: true,
    size: "large",
  },
  {
    icon: Building2,
    title: "CPO Services",
    description: "Full charge point operator services. We own, operate, and maintain your infrastructure.",
    highlight: "Low CAPEX",
    isPrimary: false,
    size: "medium",
  },
  {
    icon: Truck,
    title: "Fleet Charging",
    description: "Depot optimization, telematics integration, and TCO analytics for commercial fleets.",
    highlight: "40% Savings",
    isPrimary: true,
    size: "medium",
  },
  {
    icon: Battery,
    title: "V2G Technology",
    description: "Bidirectional charging and grid services. Transform your fleet into revenue-generating assets.",
    highlight: "Revenue",
    isPrimary: false,
    size: "large",
  },
  {
    icon: Grid3X3,
    title: "Grid Integration",
    description: "Demand response and intelligent load management with utility partnerships.",
    highlight: "OpenADR",
    isPrimary: true,
    size: "small",
  },
  {
    icon: Plug,
    title: "Turnkey Solutions",
    description: "End-to-end deployment from site assessment to ongoing support.",
    highlight: "Full Service",
    isPrimary: false,
    size: "small",
  },
];

export function SolutionsBentoHome() {
  return (
    <section id="solutions" className="py-16 relative overflow-hidden bg-background">
      {/* Ambient background */}
      <div className="absolute inset-0 bg-gradient-subtle" />
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.05) 0%, transparent 60%)',
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header - Larger, more impactful */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <ScrollReveal>
            <div className="lg:max-w-2xl">
              <motion.div 
                className="inline-flex items-center gap-2 badge-primary mb-6"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Solutions
              </motion.div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                Complete Charging{" "}
                <span className="text-gradient">Ecosystem</span>
              </h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-muted-foreground lg:max-w-md text-lg font-light leading-relaxed">
              From software to full operations—everything you need to deploy, manage, and scale your EV infrastructure.
            </p>
          </ScrollReveal>
        </div>

        {/* Bento Grid - Enhanced with larger cards and better spacing */}
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" staggerDelay={0.08}>
          {solutions.map((solution, idx) => {
            return (
              <StaggerItem key={idx}>
                <motion.div 
                  whileHover={{ scale: 1.02, y: -4 }} 
                  transition={{ duration: 0.3, ease: "easeOut" }} 
                  className="h-full"
                >
                  <Link
                    to="/solutions"
                      className={`
                        group relative overflow-hidden rounded-2xl border border-white/15 
                        bg-card
                        backdrop-blur-sm transition-all duration-500
                        p-5 md:p-6 flex flex-col h-full min-h-[180px]
                        ${solution.isPrimary ? "hover:border-primary/40" : "hover:border-accent/40"}
                      `}
                  >
                    {/* Glow effect on hover */}
                    <div
                      className={`absolute -top-32 -right-32 w-64 h-64 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${solution.isPrimary ? "bg-primary/15" : "bg-accent/15"}`}
                    />
                    
                    {/* Subtle grid pattern */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.15) 1px, transparent 0)', backgroundSize: '24px 24px' }} />

                    {/* Header */}
                    <div className="flex items-start justify-between mb-4 relative z-10">
                      <motion.div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center ${solution.isPrimary ? "bg-primary/10 border border-primary/20" : "bg-accent/10 border border-accent/20"}`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <solution.icon className={`h-5 w-5 ${solution.isPrimary ? "text-primary" : "text-accent"}`} />
                      </motion.div>
                      <motion.div
                        className="w-8 h-8 rounded-full border border-border/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                        initial={{ x: -10, y: 10 }}
                        whileHover={{ x: 0, y: 0 }}
                      >
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                      </motion.div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col relative z-10">
                      <h3 className="font-bold mb-2 tracking-tight text-card-foreground text-lg">
                        {solution.title}
                      </h3>
                      <p className="text-white/70 flex-1 text-sm leading-relaxed font-light">
                        {solution.description}
                      </p>
                    </div>

                    {/* Highlight Tag */}
                    <div className="mt-4 relative z-10">
                      <span
                        className={`inline-flex items-center text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full ${solution.isPrimary ? "bg-primary/10 text-primary border border-primary/20" : "bg-accent/10 text-accent border border-accent/20"}`}
                      >
                        {solution.highlight}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
}